// Device preferences, separate from the child's cloud-backed game progress.
import { clamp } from './util.js';

export const TOUCH_LAYOUT_KEY = 'saicraft.touch-layout.v1';
export const CONTROL_IDS = ['move', 'action', 'jump', 'zoom'];
export const orientation = (w, h) => w >= h ? 'landscape' : 'portrait';

export function cleanTouchLayouts(raw) {
  const out = { portrait: {}, landscape: {} };
  for (const mode of Object.keys(out)) for (const id of CONTROL_IDS) {
    const p = raw?.[mode]?.[id];
    if (Number.isFinite(p?.x) && Number.isFinite(p?.y))
      out[mode][id] = { x: clamp(p.x, 0, 1), y: clamp(p.y, 0, 1) };
  }
  return out;
}

export function fitControl(p, size, view) {
  const sx = size.width / 2, sy = size.height / 2;
  const left = (view.left || 0) + sx + 8, top = (view.top || 0) + sy + 8;
  const right = Math.max(left, view.width - (view.right || 0) - sx - 8);
  const bottom = Math.max(top, view.height - (view.bottom || 0) - sy - 8);
  return { x: clamp(p.x, left, right), y: clamp(p.y, top, bottom) };
}

const overlaps = (a, b) => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
const rectAt = (p, s) => ({ left: p.x - s.width / 2, right: p.x + s.width / 2, top: p.y - s.height / 2, bottom: p.y + s.height / 2 });

// Prefer the nearest free position, so a drag never loses a button off-screen or
// leaves two controls on top of each other. If there is no room, keep its old spot.
export function freeControl(p, size, view, obstacles, fallback) {
  const candidates = [p];
  for (const r of obstacles) candidates.push(
    { x: r.left - size.width / 2 - 8, y: p.y },
    { x: r.right + size.width / 2 + 8, y: p.y },
    { x: p.x, y: r.top - size.height / 2 - 8 },
    { x: p.x, y: r.bottom + size.height / 2 + 8 },
  );
  const valid = candidates.map(c => fitControl(c, size, view))
    .filter(c => !obstacles.some(r => overlaps(rectAt(c, size), r)))
    .sort((a, b) => Math.hypot(a.x - p.x, a.y - p.y) - Math.hypot(b.x - p.x, b.y - p.y));
  return valid[0] || fitControl(fallback, size, view);
}

export class TouchLayout {
  constructor(input) {
    this.input = input;
    try { this.saved = cleanTouchLayouts(JSON.parse(localStorage.getItem(TOUCH_LAYOUT_KEY))); }
    catch { this.saved = cleanTouchLayouts(); }
    this.editing = false;
    window.addEventListener('resize', () => {
      if (this.drag) this.up({ pointerId: this.drag.pointer }, true);
      input.reset();
      this.apply();
    });
    window.addEventListener('pointermove', e => this.move(e));
    window.addEventListener('pointerup', e => this.up(e));
    window.addEventListener('pointercancel', e => this.up(e, true));
  }

  targets() {
    return { move: this.input.joyBase, action: this.input.touchEl.querySelector('#tbtn-action'),
      jump: this.input.touchEl.querySelector('#tbtn-jump'), zoom: document.querySelector('.camera-controls') };
  }
  mode() { return orientation(innerWidth, innerHeight); }
  hasCustom(id) { return !!(this.editing ? this.draft : this.saved)[this.mode()][id]; }
  view() {
    // Read resolved env() values even when the controls are hidden in a menu.
    const r = document.querySelector('#touch-safe-area').getBoundingClientRect();
    return { width: innerWidth, height: innerHeight, left: r.left, top: r.top,
      right: innerWidth - r.right, bottom: innerHeight - r.bottom };
  }
  size(id, el) {
    const css = getComputedStyle(el);
    if (id === 'zoom') return css.flexDirection === 'row' ? { width: 144, height: 44 } : { width: 44, height: 144 };
    return { width: parseFloat(css.width), height: parseFloat(css.height) };
  }
  defaultPoint(id, size, v) {
    const short = v.height <= 600;
    if (id === 'move') return { x: Math.min(95, v.width * .2) + v.left, y: v.height - (short ? 145 : 170) - v.bottom };
    if (id === 'jump') return { x: v.width - 26 - size.width / 2 - v.right, y: v.height - (short ? 90 : 110) - size.height / 2 - v.bottom };
    if (id === 'action') return { x: v.width - (short ? 124 : 146) - size.width / 2 - v.right, y: v.height - (short ? 100 : 120) - size.height / 2 - v.bottom };
    // Zoom: a row under the toolbar on short landscape screens, otherwise a column at the right edge that ends above Jump.
    // Short portrait phones (e.g. 320×568) keep the column: a row up there would cover Adventure's Menu and Pause.
    const row = short && v.width > v.height;
    const jumpTop = v.height - (short ? 90 : 110) - this.size('jump', this.targets().jump).height - v.bottom;
    return { x: v.width - 16 - size.width / 2 - v.right,
      y: row ? 80 + size.height / 2 + v.top : Math.min(v.height * .44 + size.height / 2, jumpTop - 8 - size.height / 2) };
  }
  point(id) {
    const el = this.targets()[id], size = this.size(id, el), v = this.view();
    const p = (this.editing ? this.draft : this.saved)[this.mode()][id];
    return fitControl(p ? { x: p.x * v.width, y: p.y * v.height } : this.defaultPoint(id, size, v), size, v);
  }
  applyControl(id) {
    const el = this.targets()[id];
    if (!el || (id === 'move' && this.input.joy.id !== null)) return;
    // Desktop zoom buttons retain their original layout.
    el.classList.toggle('positioned-control', id !== 'zoom' || this.input.touchMode);
    let p = this.point(id);
    if (this.input.touchMode && !this.editing && this.hasCustom(id)) {
      const obstacles = [...document.querySelectorAll('.build-dock')].map(e => e.getBoundingClientRect());
      for (const [other, target] of Object.entries(this.targets()))
        if (other !== id && target && getComputedStyle(target).display !== 'none') obstacles.push(target.getBoundingClientRect());
      p = freeControl(p, this.size(id, el), this.view(), obstacles, this.defaultPoint(id, this.size(id, el), this.view()));
    }
    el.style.setProperty('--control-x', p.x + 'px');
    el.style.setProperty('--control-y', p.y + 'px');
  }
  apply() {
    const label = document.querySelector('.control-layout-orientation');
    if (label) label.textContent = this.mode();
    for (const [id, el] of Object.entries(this.targets())) {
      if (!el) continue;
      el.dataset.touchControl = id;
      if (!el.layoutBound) {
        el.layoutBound = true;
        el.addEventListener('pointerdown', e => this.down(e, id), true);
        el.addEventListener('lostpointercapture', e => this.up(e, true));
      }
      this.applyControl(id);
    }
  }
  begin() {
    this.input.reset();
    this.draft = structuredClone(this.saved);
    this.editing = true;
    document.body.classList.add('controls-editing');
    this.apply();
  }
  finish(save) {
    if (!this.editing) return true;
    if (save) {
      try { localStorage.setItem(TOUCH_LAYOUT_KEY, JSON.stringify(this.draft)); }
      catch { return false; }
      this.saved = cleanTouchLayouts(this.draft);
    }
    this.drag = null;
    this.editing = false;
    document.body.classList.remove('controls-editing');
    this.input.reset();
    this.apply();
    return true;
  }
  resetLayout() {
    if (!this.editing) return;
    this.draft[this.mode()] = {};
    this.drag = null;
    this.apply();
  }
  down(e, id) {
    if (!this.editing) return;
    e.preventDefault(); e.stopImmediatePropagation();
    if (this.drag) return;
    const p = this.point(id), el = this.targets()[id];
    this.drag = { id, pointer: e.pointerId, dx: e.clientX - p.x, dy: e.clientY - p.y,
      mode: this.mode(), before: this.draft[this.mode()][id], el };
    el.setPointerCapture(e.pointerId);
  }
  move(e) {
    const d = this.drag;
    if (!d || d.pointer !== e.pointerId || d.mode !== this.mode()) return;
    e.preventDefault();
    const obstacles = Object.entries(this.targets()).filter(([id]) => id !== d.id)
      .map(([, el]) => el.getBoundingClientRect());
    for (const el of document.querySelectorAll('.control-layout-card, .build-dock')) obstacles.push(el.getBoundingClientRect());
    const v = this.view(), p = freeControl({ x: e.clientX - d.dx, y: e.clientY - d.dy },
      this.size(d.id, d.el), v, obstacles, this.point(d.id));
    this.draft[d.mode][d.id] = { x: p.x / v.width, y: p.y / v.height };
    this.applyControl(d.id);
  }
  up(e, cancel = false) {
    const d = this.drag;
    if (!d || d.pointer !== e.pointerId) return;
    this.drag = null;
    if (cancel) {
      if (d.before) this.draft[d.mode][d.id] = d.before;
      else delete this.draft[d.mode][d.id];
      this.applyControl(d.id);
    }
  }
}
